import { Agent, Exception, postCloneOrUpdateAgent } from '@nemuru-eng/js-api-client';
import { useState } from 'react';

import { FormValues } from '@/components/widgets/AgentDetailActions/components/CloneAgent/CloneAgent';
import { NewAgent } from '@/components/widgets/AgentDetailActions/components/CloneAgent/components/Agents/types/NewAgent';
import { NewAgentCloned } from '@/components/widgets/AgentDetailActions/components/CloneAgent/components/Agents/types/NewAgentCloned';
import { NewAgentState } from '@/components/widgets/AgentDetailActions/components/CloneAgent/components/Agents/types/NewAgentState';
import { useGetMerchantStructureById } from '@/hooks/requests/merchant/useGetMerchantStructureById';

interface UseCloseOrUpdateAgentsProps {
  clonedAgent: Agent;
  merchantId: string;
}

export const useCloneOrUpdateAgents = ({ clonedAgent, merchantId }: UseCloseOrUpdateAgentsProps) => {
  const { revalidate } = useGetMerchantStructureById(merchantId);

  const [state, setState] = useState<State>({
    isSubmitting: false,
  });

  const onSubmit = async (formData: FormValues, agents: NewAgent[]) => {
    const agentsCloned = agents.map(
      (agent): NewAgentCloned => ({
        ...agent,
        clonedAgentId: clonedAgent.id,
        isSubmitting: false,
        isCloned: false,
        isUpdated: false,
        hasError: false,
      }),
    );

    setState((s) => ({
      ...s,
      isSubmitting: true,
      agentsCloned,
    }));

    for await (const agent of agentsCloned) {
      setAgent({ ...agent, isSubmitting: true });
      try {
        const { cloned } = await postCloneOrUpdateAgent({
          agentId: agent.agentId,
          clonedAgentId: clonedAgent.id,
          agentName: agent.agentName,
          ...(agent.agentEmail && { agentEmail: agent.agentEmail }),
          channelId: agent.channelId,
          options: {
            configuration: !!formData.cloneConfiguration,
            agentIdInLenderContext: !!formData.cloneAgentInLenderContext,
            pricings: !!formData.clonePricings,
            user: {
              create: !!formData.cloneCreateUser,
              sendPasswordRecovery: !!formData.sendPasswordRecovery,
            },
          },
        });
        if (cloned) {
          setAgent({ ...agent, clonedAgentId: clonedAgent.id, isCloned: true });
        } else {
          setAgent({ ...agent, clonedAgentId: clonedAgent.id, isUpdated: true });
        }
        revalidate();
      } catch (e) {
        let errorMessage = 'Unknown error';
        if (e instanceof Exception) {
          errorMessage = e.message;
        }
        console.log(e);
        setAgent({ ...agent, hasError: true, errorMessage });
      }
    }
    setState((s) => ({ ...s, isSubmitting: false }));
  };

  // const postCloneOrUpdateAgent = async (payload: PostCloneOrUpdateAgent): Promise<{ cloned: boolean }> => {
  //   return new Promise((resolve, reject) => {
  //     setTimeout(() => {
  //       console.log('resolving for....', payload);
  //       const p = Math.random();
  //       if (p > 0.7) {
  //         return reject(new ConflictException('El agentId ya existe', '409', 'https://example.com'));
  //       }
  //       if (p > 0.5) {
  //         return resolve({ cloned: true });
  //       }
  //       return resolve({ cloned: false });
  //     }, 3000);
  //   });
  // };

  const setAgent = (agentState: NewAgentState): void => {
    setState((s) => ({
      ...s,
      agentsCloned: s.agentsCloned?.map((stateAgent) => {
        if (agentState.agentId !== stateAgent.agentId) return stateAgent;
        return {
          ...stateAgent,
          ...agentState,
        };
      }),
    }));
  };

  const { isSubmitting, agentsCloned } = state;

  return { onSubmit, isSubmitting, agentsCloned };
};

interface State {
  isSubmitting: boolean;
  agentsCloned?: NewAgentCloned[];
}
